import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeChoice = 'light' | 'dark' | 'system';
/** full = every effect; lite = short fades only (slow devices); off = no motion. */
export type MotionChoice = 'full' | 'lite' | 'off';

interface Experience {
  theme: ThemeChoice;
  motion: MotionChoice;
  sound: boolean;
  /** Sound volume, 0–1 (default 0.6). */
  volume: number;
  set(p: Partial<Pick<Experience, 'theme' | 'motion' | 'sound' | 'volume'>>): void;
}

const reducedByOs = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Per-person look and feel, kept in this browser. Sounds are on by default (owner, 2026-11-03; once switched on for
 * everyone, then each person's choice); motion follows the OS "reduce motion" setting.
 */
export const useExperience = create<Experience>()(
  persist(
    (set) => ({ theme: 'light', motion: reducedByOs() ? 'off' : 'full', sound: true, volume: 0.6, set: (p) => set(p) }),
    {
      name: 'ruby.experience',
      version: 3,
      // v2 switched sounds on for everyone once; v3 adds the volume (kept as each person left it otherwise).
      migrate: (old, v) => ({ ...(old as object), ...(v < 2 ? { sound: true } : {}), volume: (old as { volume?: number }).volume ?? 0.6 }) as Experience,
    },
  ),
);

export const motionOn = () => useExperience.getState().motion !== 'off';
export const fullMotion = () => useExperience.getState().motion === 'full';

/** Mirrors the choices onto <html data-theme data-motion> so CSS can react. */
export function useApplyExperience() {
  const { theme, motion } = useExperience();
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      document.documentElement.dataset.theme = theme === 'system' ? (mq.matches ? 'dark' : 'light') : theme;
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [theme]);
  useEffect(() => {
    document.documentElement.dataset.motion = motion;
  }, [motion]);
}
